import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { FeatureCard, FeatureIcon, CardTitle, CardText, Lead, accentAt, AccreditationNote } from '../components/ui';

const SITE = 'https://qasc.it';

// The three fund channels named on the original page.
const funds = [
  {
    icon: <AccountBalanceRoundedIcon />,
    title: 'Fondi interprofessionali',
    text: 'Bandi finanziati con fondi interprofessionali quali Fondimpresa.',
  },
  {
    icon: <MapRoundedIcon />,
    title: 'Fondi regionali',
    text: 'Bandi derivanti da fondi regionali.',
  },
  {
    icon: <PublicRoundedIcon />,
    title: 'Bandi europei',
    text: 'Bandi europei per la formazione e lo sviluppo delle competenze.',
  },
];

function FormazioneFinanziataPage() {
  return (
    <PageLayout
      seo={{
        title: 'Formazione Finanziata per l’Azienda | QAS Consulting',
        description:
          'Progettiamo e organizziamo la formazione finanziata: fondi interprofessionali come Fondimpresa, bandi regionali ed europei, con erogazione diretta dei corsi e gestione della pratica fino alla rendicontazione finale.',
        canonical: `${SITE}/formazione-finanziata`,
      }}
      hero={{
        kicker: 'Formazione · Finanziata',
        title: 'Formazione finanziata per l’azienda',
        subtitle:
          'L’opportunità di crescita e sviluppo delle risorse umane nelle vostre aziende.',
      }}
    >
      {/* (1) Come lavoriamo */}
      <Section tone="light">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionHeading
                align="left"
                kicker="La formazione finanziata"
                title="Noi la progettiamo e la organizziamo"
                sx={{ mb: 3 }}
              />
              <Lead>
                Della formazione finanziata tutti ne parlano, ma noi la progettiamo e la
                organizziamo in maniera efficace, per consolidare e accrescere la competenza e la
                professionalità dei vostri collaboratori.
              </Lead>
              <Typography sx={{ fontSize: '15.5px', lineHeight: 1.85, color: '#48606d', mb: 2 }}>
                QAS Consulting offre alle aziende clienti aderenti a uno dei fondi — o che
                intendono iscriversi — una partnership nell’accesso e nell’utilizzo delle risorse
                accantonate, attraverso la redazione di progetti formativi che comprendono
                l’erogazione diretta dei corsi finanziabili e la gestione della pratica fino alla
                rendicontazione finale.
              </Typography>
              <Typography sx={{ fontSize: '15.5px', lineHeight: 1.85, color: '#48606d', mb: 2 }}>
                Nel caso in cui le aziende non siano ancora iscritte, forniamo tutta l’assistenza
                per l’iscrizione e per comprendere a pieno tutte le potenzialità che ne derivano.
              </Typography>
              <Typography sx={{ fontSize: '15.5px', lineHeight: 1.85, color: '#48606d' }}>
                Il progetto è da considerarsi “su misura”: viene costruito in base alle esigenze
                segnalate dall’azienda, dando allo stesso anche le caratteristiche necessarie per
                essere ben accolto dai fondi che lo finanziano.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {funds.map((m, i) => (
                  <Grid item xs={12} key={m.title}>
                    <FeatureCard sx={{ display: 'flex', alignItems: 'flex-start', gap: '18px', p: '24px' }}>
                      <FeatureIcon accent={accentAt(i)} sx={{ mb: 0, flexShrink: 0 }}>
                        {m.icon}
                      </FeatureIcon>
                      <Box>
                        <CardTitle sx={{ mb: 0.5 }}>{m.title}</CardTitle>
                        <CardText>{m.text}</CardText>
                      </Box>
                    </FeatureCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* (2) CTA */}
      <Section tone="dark">
        <Container maxWidth="lg">
          <SectionHeading
            tone="dark"
            kicker="Scrivici"
            title="Scrivici per trovare il fondo adatto a te!"
            subtitle="Con i nostri professionisti siamo in grado di gestire bandi interprofessionali, regionali ed europei: raccontaci le esigenze formative della tua azienda."
          />
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              component={RouterLink}
              to="/contatti"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: '"Sora", sans-serif',
                backgroundColor: '#ffffff',
                color: '#00425c',
                px: '30px',
                py: '13px',
                borderRadius: '999px',
                fontWeight: 600,
                fontSize: '15px',
                textDecoration: 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { backgroundColor: '#e9f1f6', transform: 'translateY(-2px)' },
              }}
            >
              Invia la tua richiesta
            </Typography>
          </Box>
          <AccreditationNote dark />
        </Container>
      </Section>
    </PageLayout>
  );
}

export default FormazioneFinanziataPage;
